import React from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import isEqual from 'date-fns/isEqual';

import { formatDateTime } from './../../utils/dateUtils';
import {
    timePeriodOptions,
    mapPeriodTypeToText, DeadlineOption,
} from './../../utils/obligationUtils';

import { mdiInformationOutline } from '@mdi/js';
import Icon from '@mdi/react';

import DescriptionList, {
  Description,
} from 'components/UIComponents/DescriptionList';
import Tooltip from 'components/UIComponents/Tooltip';
import { Content, Heading } from 'components/UIComponents/Typography';

export default () => {
  const { t } = useTranslation();

  const {
    submissionDetail: {
      initialDueDate,
      dueDate,
      obligation = {}
    },
  } = useSelector(state => state.submissionDetail);

  const {
    periodType,
    timePeriod,
    periodMultiplier,
    firstDueDate,
    terminationDate,
    timeSeriesDto,
    deadlineOption
  } = obligation;

  return (
    <>
      <Heading subtitle className="is-uppercase fw-bold">
        {t('datagate.external.submissionDetail.subtitle.submission')}
      </Heading>
      <Content>
        <DescriptionList>
          <Description
            id="obligation-detail-first-due-date"
            aria-label="Obligation detail first due date"
            field={t('datagate.external.submissionDetail.label.from')}
            value={firstDueDate && formatDateTime(firstDueDate)}
          />
          {terminationDate && (
            <Description
              id="obligation-detail-termination-date"
              aria-label="Obligation detail termination date"
              field={t('datagate.external.submissionDetail.label.until')}
              value={formatDateTime(terminationDate)}
            />
          )}
          <Description
            id="obligation-detail-time-period"
            aria-label="Obligation detail time period"
            field={
                <span style={{display: 'inline-flex', alignItems: 'baseline', gap: '4px'}}>
                {t('datagate.external.submissionDetail.label.timePeriod')}{' '}
                <Tooltip
                  position="top"
                  html={
                    <div>
                      {t(
                        'datagate.external.submissionDetail.tooltip.timePeriod'
                      )}
                    </div>
                  }
                >
                  <Icon path={mdiInformationOutline} size={0.75} />
                </Tooltip>
              </span>
            }
            value={`${
              periodType ? `${t(mapPeriodTypeToText(periodType))}` : ''
            }${
              periodType !== 'SINGLE'
                ? `, ${t(
                    'datagate.external.submissionDetail.timePeriod.every'
                  )} ${periodMultiplier} ${
                    timePeriod
                      ? t(
                          timePeriodOptions.find(
                            ({ value }) => value === timePeriod
                          )?.label
                        )
                      : ''
                  }`
                : ''
            }`}
          />
          <Description
            id="obligation-detail-timeseries"
            aria-label="Obligation detail time series"
            field={
                <span style={{display: 'inline-flex', alignItems: 'baseline', gap: '4px'}}>
                {t('datagate.external.submissionDetail.label.timeSeries')}{' '}
                    <Tooltip
                        position="top"
                        html={
                            <div>
                                {t(
                                    'datagate.external.submissionDetail.tooltip.timeSeries'
                                )}
                            </div>
                        }
                    >
                  <Icon path={mdiInformationOutline} size={0.75}/>
                </Tooltip>
              </span>
            }
            value={timeSeriesDto?.name ?? ''}
          />
            <Description
            id="obligation-detail-submission-initial-due-date"
            field={t('datagate.external.submissionDetail.label.initialDueDate')}
            value={initialDueDate && formatDateTime(initialDueDate)}
          />
          {dueDate && !isEqual(new Date(dueDate), new Date(initialDueDate)) && (
            <Description
              id="obligation-detail-submission-due-date"
              field={t('datagate.external.submissionDetail.label.dueDate')}
              value={dueDate && formatDateTime(dueDate)}
            />
          )}
        </DescriptionList>
          {deadlineOption === DeadlineOption.PREVIOUS && (
              <p>
                  {t('datagate.external.submissionDetail.message.onPreviousWorkDay')}
              </p>
          )}

          {deadlineOption === DeadlineOption.FOLLOWING && (
              <p>
                  {t('datagate.external.submissionDetail.message.onFollowingWorkDay')}
              </p>
          )}
        <p>{t('datagate.external.submissionDetail.label.submissionDateDisclaimer')}</p>
      </Content>
    </>
  );
};
